import React, { useState, useMemo } from 'react'
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  TextInput,
} from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Icon, FilterHeader, Screen, GenericSelectionModal, GenderSwitch } from '@components'
import { AgeRangeSlider } from './components/AgeRangeSlider'
import { Theme } from '@theme'
import { ScreenContactProps } from '@navigation'
import { useListingContactsStore } from './store/useListingContactsStore'
import { getCommonStyles, getFormatedList, GroupedItem, groupItemsBySector, SelectionItem } from '@utils'
import {
  useGetContinents,
  useGetProfessionSectors,
  usePaginateCategories,
  usePaginateCities,
  usePaginateColorLabels,
  usePaginateCountries,
  usePaginateHashtags,
  usePaginateLanguages,
  usePaginateProfessions,
  usePaginateRegions,
  useScreen,
} from '@hooks'
import { useTranslatedContextStore, useTranslatedEmotionalContextStore, useTranslatedRelationshipStatusStore } from '@store'
import { useLanguage } from '@context'

interface FilterItem {
  id: string
  label: string
  value?: string
}

interface FilterSection {
  id: string
  title: string
  icon: string
  items?: FilterItem[]
  values?: SelectionItem[]
}

export const ListingContactsFilters: React.FC<ScreenContactProps<'ListingContactsFilters'>> = ({ navigation, route }) => {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const {isMobile} = useScreen()
  const styles = getStyles(theme, !isMobile)
  const commonStyles = getCommonStyles(theme)

  const { isSuggested } = route.params || {}

  const [searchText, setSearchText] = useState('')
  const [colorLabelSearch, setColorLabelSearch] = useState('')
  const [residenceModalSearch, setResidenceModalSearch] = useState('')
  const [lieuDeVieExpanded, setLieuDeVieExpanded] = useState(true)
  const [isOriginModalVisible, setIsOriginModalVisible] = useState(false)
  const [isNationalitiesModalVisible, setIsNationalitiesModalVisible] = useState(false)
  const [isLivedPlacesModalVisible, setIsLivedPlacesModalVisible] = useState(false)
  const [isAddressModalVisible, setIsAddressModalVisible] = useState(false)
  const [isResidenceContinentModalVisible, setIsResidenceContinentModalVisible] = useState(false)
  const [isResidenceCountryModalVisible, setIsResidenceCountryModalVisible] = useState(false)
  const [isResidenceRegionModalVisible, setIsResidenceRegionModalVisible] = useState(false)
  const [isProfessionsModalVisible, setIsProfessionsModalVisible] = useState(false)
  const [isHashtagModalVisible, setIsHashtagModalVisible] = useState(false)
  const [isCategoryModalVisible, setIsCategoryModalVisible] = useState(false)
  const [isContextModalVisible, setIsContextModalVisible] = useState(false)
  const [isEmotionalContextModalVisible, setIsEmotionalContextModalVisible] = useState(false)
  const [isRelationshipStatusModalVisible, setIsRelationshipStatusModalVisible] = useState(false)
  const [isRelationalColorModalVisible, setIsRelationalColorModalVisible] = useState(false)
  const [isLanguageModalVisible, setIsLanguageModalVisible] = useState(false)
  const [currentLocation, setCurrentLocation] = useState<{ city: string; country: string } | null>(null)

  const countryListSearch = useMemo(
    () =>
      isOriginModalVisible || isNationalitiesModalVisible
        ? searchText
        : isResidenceCountryModalVisible
          ? residenceModalSearch
          : '',
    [
      isOriginModalVisible,
      isNationalitiesModalVisible,
      isResidenceCountryModalVisible,
      searchText,
      residenceModalSearch,
    ],
  )
  const cityListSearch = useMemo(
    () => (isAddressModalVisible ? residenceModalSearch : isLivedPlacesModalVisible ? searchText : ''),
    [isAddressModalVisible, isLivedPlacesModalVisible, residenceModalSearch, searchText],
  )

  // Store du système listingContacts (indépendant d'AddContacts)
  const {
    localFilters: filters,
    setLocalFilters: setFilters,
    getActiveFiltersCount,
    clearAllFilters,
  } = useListingContactsStore()

  // Used to cascade residence geography reference-data dropdowns:
  // - continent -> countries/regions/cities
  // - country -> regions/cities
  // - region -> cities
  // When multiple items are selected, we build a UNION (OR) across their children.
  const selectedResidenceContinentCodes = (filters.residenceContinents ?? []).map((c) => c.id).filter(Boolean)
  const selectedResidenceCountryCodes = (filters.residenceCountries ?? []).map((c) => c.id).filter(Boolean)
  const selectedResidenceRegionIds = (filters.residenceRegions ?? []).map((r) => r.id).filter(Boolean)

  // Hierarchy for the city dropdown:
  // regionIds (if any) > countryCodes (if any) > continentCodes
  const cityRegionIds = selectedResidenceRegionIds.length > 0 ? selectedResidenceRegionIds : undefined
  const cityCountryCodes =
    selectedResidenceRegionIds.length > 0
      ? undefined
      : selectedResidenceCountryCodes.length > 0
        ? selectedResidenceCountryCodes
        : undefined
  const cityContinentCodes =
    selectedResidenceRegionIds.length > 0 || selectedResidenceCountryCodes.length > 0
      ? undefined
      : selectedResidenceContinentCodes.length > 0
        ? selectedResidenceContinentCodes
        : undefined

  // Hierarchy for the region dropdown:
  // countryCodes (if any) > continentCodes
  const regionCountryCodes = selectedResidenceCountryCodes.length > 0 ? selectedResidenceCountryCodes : undefined
  const regionContinentCodes = selectedResidenceCountryCodes.length > 0 ? undefined : selectedResidenceContinentCodes.length > 0 ? selectedResidenceContinentCodes : undefined

  const { data: continentsData = [], isFetching: isFetchingContinents } = useGetContinents(currentLanguage)
  const { flatData: categories, isFetching: isFetchingCategories, isFetchingNextPage: isFetchingNextPageCategories, fetchNextPage: fetchNextPageCategories, hasNextPage: hasNextPageCategories } = usePaginateCategories(searchText)
  const { flatData: hashtags, isFetching: isFetchingHashtags, isFetchingNextPage: isFetchingNextPageHashtags, fetchNextPage: fetchNextPageHashtags, hasNextPage: hasNextPageHashtags } = usePaginateHashtags(searchText)
  const { flatData: allLanguages, isFetching: isFetchingLanguages, isFetchingNextPage: isFetchingNextPageLanguages, fetchNextPage: fetchNextPageLanguages, hasNextPage: hasNextPageLanguages } = usePaginateLanguages({ search: searchText, lang: currentLanguage })
  const { flatData: cities, isFetching: isFetchingCities, isFetchingNextPage: isFetchingNextPageCities, fetchNextPage: fetchNextPageCities, hasNextPage: hasNextPageCities } = usePaginateCities({
    search: cityListSearch,
    lang: currentLanguage,
    // Constrain only while selecting "residence city" (not for livedPlaces modal).
    regionIds: isAddressModalVisible ? cityRegionIds : undefined,
    countries: isAddressModalVisible ? cityCountryCodes : undefined,
    continents: isAddressModalVisible ? cityContinentCodes : undefined,
  })
  const { flatData: countries, isFetching: isFetchingCountries, isFetchingNextPage: isFetchingNextPageCountries, fetchNextPage: fetchNextPageCountries, hasNextPage: hasNextPageCountries } = usePaginateCountries({
    search: countryListSearch,
    lang: currentLanguage,
    // Only constrain countries list while selecting "residence country".
    continents: isResidenceCountryModalVisible ? (selectedResidenceContinentCodes.length > 0 ? selectedResidenceContinentCodes : undefined) : undefined,
  })
  const { flatData: regions, isFetching: isFetchingRegions, isFetchingNextPage: isFetchingNextPageRegions, fetchNextPage: fetchNextPageRegions, hasNextPage: hasNextPageRegions } = usePaginateRegions({
    lang: currentLanguage,
    search: isResidenceRegionModalVisible ? residenceModalSearch : '',
    // Only constrain regions list while selecting "residence region".
    countries: isResidenceRegionModalVisible ? regionCountryCodes : undefined,
    continents: isResidenceRegionModalVisible ? regionContinentCodes : undefined,
  })
  const continentSelectionItems = useMemo(
    () =>
      continentsData.map((c) => ({
        id: c.code,
        name: c.name ?? c.nameFr ?? c.nameEn,
      })),
    [continentsData],
  )
  const { flatData: colorLabels, isFetching: isFetchingColorLabels, isFetchingNextPage: isFetchingNextPageColorLabels, fetchNextPage: fetchNextPageColorLabels, hasNextPage: hasNextPageColorLabels } = usePaginateColorLabels(colorLabelSearch)

  const { contexts: groupedContexts } = useTranslatedContextStore()
  const { contexts: availableEmotionalContexts } = useTranslatedEmotionalContextStore()
  const { statuses: availableRelationshipStatus } = useTranslatedRelationshipStatusStore()

  // Fetch profession sectors and professions from API
  const { data: professionSectors = [] } = useGetProfessionSectors()
  const { flatData: professions, isFetching: isFetchingProfessions, isFetchingNextPage: isFetchingNextPageProfessions, fetchNextPage: fetchNextPageProfessions, hasNextPage: hasNextPageProfessions } = usePaginateProfessions(searchText)
  
  // Group professions by sector
  const groupedProfessions: GroupedItem[] = useMemo(() => {
    return groupItemsBySector(professionSectors, professions)
  }, [professionSectors, professions])
  
  const sections = [
    {
      id: 'sexe',
      title: t('filters.gender'),
      icon: 'user',
    },
    {
      id: 'age',
      title: t('filters.age'),
      icon: 'calendar',
    },
    {
      id: 'lieu-de-vie',
      title: t('filters.residence'),
      icon: 'mapPin',
      values: [
        ...(filters.residenceContinents || []),
        ...(filters.residenceCountries || []),
        ...(filters.residenceRegions || []),
        ...(filters.residence || []),
      ]
    },
    {
      id: 'lieux-vecus',
      title: t('filters.livedPlaces'),
      icon: 'map',
      values: filters.livedPlaces,
    },
    /*{
      id: 'location',
      title: 'Position actuelle',
      icon: 'mapPin',
      items: [
        {
          id: 'position-actuelle',
          label: 'Utiliser ma position actuelle',
          value: '',
        },
      ]
    },*/
    {
      id: 'origine',
      title: t('filters.origin'),
      icon: 'globe',
      values: filters.origins,
    },
    {
      id: 'nationalities',
      title: t('filters.nationality'),
      icon: 'globe',
      values: filters.nationalities,
    },
    {
      id: 'fonction',
      title: t('filters.profession'),
      icon: 'work',
      values: filters.professions
    },
    ...(isSuggested ? [] : [
      {
        id: 'context',
        title: t('filters.meetingContext'),
        icon: 'work',
        values: filters.contexts,
      },
      {
        id: 'emotional-context',
        title: t('filters.associatedEmotion'),
        icon: 'heart',
        values: filters.emotionalContexts,
      },
      {
        id: 'relationship-status',
        title: t('filters.relationshipEvolution'),
        icon: 'people',
        values: filters.relationshipStatus,
      },
      {
        id: 'relational-color',
        title: t('filters.associatedColor'),
        icon: 'link2',
        values: filters.relationalColor,
      },
    ]),
    {
      id: 'langue',
      title: t('filters.language'),
      icon: 'globe',
      values: filters.languages,
    }, isSuggested ? null : (
      {
        id: 'categorie',
        title: t('filters.category'),
        icon: 'taggedPictures',
        values: filters.categories,
      }
    ),
    isSuggested ? (
      {
        id: 'degree',
        title: t('contact.filter.category.degree') || 'Degree',
        icon: 'link2',
      }
    ) : (
      {
        id: 'hashtag',
        title: t('filters.hashtag'),
        icon: 'htag',
        values: filters.hashtags,
      }
    ),
  ].filter(section => section !== null)

  const getCurrentLocation = async () => {
    const response = await fetch('https://ipinfo.io/json')
    const data = await response.json()
    return {
      city: data.city,
      country: data.country,
    }
  }

  const toggleSection = (sectionId: string) => {
    // Open every picker with a clean search so a term typed in one filter
    // (e.g. "suisse" in Nationalité) doesn't leak into the next one.
    setSearchText('')
    setResidenceModalSearch('')
    setColorLabelSearch('')

    // Condition pour les sections qui ont un seul élément et doivent déclencher directement l'action
    //const singleActionSections = ['sexe', 'age', 'origine', 'nationalities', 'langue', 'categorie', 'hashtag', 'context', 'emotional-context', 'relationship-status', 'relational-color', 'position', 'fonction']

    // if (singleActionSections.includes(sectionId)) {
    if (sectionId === 'origine') {
      setIsOriginModalVisible(true)
    } else if (sectionId === 'lieux-vecus') {
      setIsLivedPlacesModalVisible(true)
    } else if (sectionId === 'lieu-de-vie') {
      setLieuDeVieExpanded((e) => !e)
    } else if (sectionId === 'nationalities') {
      setIsNationalitiesModalVisible(true)
    } else if (sectionId === 'fonction') {
      setIsProfessionsModalVisible(true)
    } else if (sectionId === 'hashtag') {
      setIsHashtagModalVisible(true)
    } else if (sectionId === 'categorie') {
      setIsCategoryModalVisible(true) 
    } else if (sectionId === 'context') {
      setIsContextModalVisible(true)
    } else if (sectionId === 'emotional-context') {
      setIsEmotionalContextModalVisible(true)
    } else if (sectionId === 'relationship-status') {
      setIsRelationshipStatusModalVisible(true)
    } else if (sectionId === 'relational-color') {
      setIsRelationalColorModalVisible(true)
    } else if (sectionId === 'langue') {
      setIsLanguageModalVisible(true)
    }
    
    if (sectionId === 'location') {
      getCurrentLocation().then(currentLocation => {
        setCurrentLocation(currentLocation)
      })
    }
  }

  const openResidenceModal = (kind: 'continent' | 'country' | 'region' | 'city') => {
    setResidenceModalSearch('')
    setIsResidenceContinentModalVisible(kind === 'continent')
    setIsResidenceCountryModalVisible(kind === 'country')
    setIsResidenceRegionModalVisible(kind === 'region')
    setIsAddressModalVisible(kind === 'city')
  }

  const renderSection = (section: FilterSection) => {
    if (section.id === 'lieu-de-vie') {
      const summary = [
        ...(filters.residenceContinents || []),
        ...(filters.residenceCountries || []),
        ...(filters.residenceRegions || []),
        ...(filters.residence || []),
      ]
      return (
        <View key={section.id} style={styles.sectionContainer}>
          <TouchableOpacity
            style={styles.sectionHeader}
            onPress={() => setLieuDeVieExpanded((e) => !e)}
            activeOpacity={0.7}
          >
            <View style={commonStyles.flex}>
              <View style={styles.sectionHeaderLeft}>
                <Icon name={section.icon as any} size={20} color={theme.colors.primary} />
                <Text style={styles.sectionTitle}>{section.title}</Text>
              </View>
              {summary.length > 0 && (
                <Text style={styles.sectionValues}>{getFormatedList(summary.map((v) => v.name))}</Text>
              )}
            </View>
            <Icon
              name="arrow"
              size={16}
              color={theme.colors.greyBlue}
              containerStyle={{
                transform: [{ rotate: lieuDeVieExpanded ? '90deg' : '0deg' }],
              }}
            />
          </TouchableOpacity>
          {lieuDeVieExpanded && (
            <View style={styles.residenceAccordionBody}>
              {(
                [
                  { kind: 'continent' as const, label: t('filters.residenceContinent'), values: filters.residenceContinents },
                  { kind: 'country' as const, label: t('filters.residenceCountry'), values: filters.residenceCountries },
                  { kind: 'region' as const, label: t('filters.residenceRegion'), values: filters.residenceRegions },
                  { kind: 'city' as const, label: t('filters.residenceCity'), values: filters.residence },
                ] as const
              ).map((row) => (
                <TouchableOpacity
                  key={row.kind}
                  style={[styles.residenceSubRow, row.kind === 'city' && { borderBottomWidth: 0 }]}
                  onPress={() => openResidenceModal(row.kind)}
                  activeOpacity={0.7}
                >
                  <View style={styles.residenceSubRowText}>
                    <Text style={styles.residenceSubRowLabel}>{row.label}</Text>
                    {row.values && row.values.length > 0 && (
                      <Text style={styles.residenceSubRowValues} numberOfLines={2}>
                        {getFormatedList(row.values.map((v) => v.name))}
                      </Text>
                    )}
                  </View>
                  <Icon name="arrow" size={14} color={theme.colors.greyBlue} />
                </TouchableOpacity>
              ))}
            </View>
          )}
        </View>
      )
    }

    return (
      <View key={section.id} style={styles.sectionContainer}>
        <TouchableOpacity
          style={styles.sectionHeader}
          onPress={() => toggleSection(section.id)}
          activeOpacity={0.7}
        >
          <View style={commonStyles.flex}>
            <View style={styles.sectionHeaderLeft}>
              <Icon name={section.icon as any} size={20} color={theme.colors.primary} />
              <Text style={styles.sectionTitle}>{section.title}</Text>
            </View>
            {section.values && section.values.length > 0 && (
              <Text style={styles.sectionValues}>{getFormatedList(section.values.map(value => value.name))}</Text>
            )}
          </View>
          {section.id === 'sexe' && (
            <GenderSwitch isSmall gender={filters.gender} onGenderSelect={(gender) => setFilters({ ...filters, gender: filters.gender === gender ? undefined : gender })} />
          )}
          {section.id === 'age' && (
            <View style={styles.ageInputContainer}>
              <AgeRangeSlider
                value={filters.ageRange}
                onChange={(ageRange) => setFilters({ ...filters, ageRange })}
              />
            </View>
          )}
          {section.id === 'degree' && (
            <View style={styles.degreeContainer}>
              {[
                { value: 1, label: t('addContact.degree1') },
                { value: 2, label: t('addContact.degree2') },
                { value: 3, label: t('addContact.degree3') },
              ].map(({ value, label }) => {
                const isActive = (filters.degree ?? null) === value
                return (
                  <TouchableOpacity
                    key={String(value)}
                    style={[styles.degreeChip, isActive && styles.degreeChipActive]}
                    onPress={() =>
                      setFilters({ ...filters, degree: isActive ? null : value })
                    }
                  >
                    <Text style={[styles.degreeChipText, isActive && styles.degreeChipTextActive]}>{label}</Text>
                  </TouchableOpacity>
                )
              })}
            </View>
          )}
          {!['sexe', 'age', 'degree'].includes(section.id) && (
            <Icon
              name="arrow"
              size={16}
              color={theme.colors.greyBlue}
            />
          )}
        </TouchableOpacity>
      </View>
    )
  }

  return (
    <Screen style={styles.screen}>
      <FilterHeader
        title={t('filters.title')}
        iconLeft={!isMobile? undefined : "arrowBack"}
        iconColor={theme.colors.secondary}
        onPressLeft={() => navigation.goBack()}
        filterCount={getActiveFiltersCount()}
        onClearFilters={clearAllFilters}
      />
      
      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        {sections.map(renderSection)}
      </ScrollView>

      <GenericSelectionModal
        isVisible={isOriginModalVisible || isAddressModalVisible || isLivedPlacesModalVisible || isNationalitiesModalVisible}
        setIsVisible={(val) => {
          if (isOriginModalVisible) {
            setIsOriginModalVisible(val)
          } else if (isAddressModalVisible) {
            setIsAddressModalVisible(val)
          } else if (isLivedPlacesModalVisible) {
            setIsLivedPlacesModalVisible(val)
          } else if (isNationalitiesModalVisible) {
            setIsNationalitiesModalVisible(val)
          }
        }}
        title={
          isOriginModalVisible
            ? t('filters.origin')
            : isLivedPlacesModalVisible
              ? t('filters.livedPlaces')
              : isNationalitiesModalVisible
                ? t('filters.nationality')
                : t('filters.residenceCity')
        }
        placeholder={t('filters.searchPlace')}
        availableItems={(isAddressModalVisible || isLivedPlacesModalVisible) ? cities.map((city) => ({
          id: city.id,
          name: city.name ?? city.nameFr,
        })) : countries.map((country) => ({
          id: country.code,
          name: country.name ?? country.nameFr,
        }))}
        selectedItems={isLivedPlacesModalVisible ? filters.livedPlaces : (isNationalitiesModalVisible ? filters.nationalities : isOriginModalVisible ? filters.origins : filters.residence) || []}
        onSelectionChange={(items) => {
          if (isOriginModalVisible) {
            setFilters({ ...filters, origins: items })
          } else if (isAddressModalVisible) {
            setFilters({
              ...filters,
              residence: items,
              residenceContinents: [],
              residenceCountries: [],
              residenceRegions: [],
            })
          } else if (isLivedPlacesModalVisible) {
            setFilters({ ...filters, livedPlaces: items })
          } else if (isNationalitiesModalVisible) {
            setFilters({ ...filters, nationalities: items })
          }
        }}
        multiSelect
        fitParent={!isMobile}
        isFetching={isFetchingCities || isFetchingCountries}
        fetchNextPage={() => {
          if (isAddressModalVisible || isLivedPlacesModalVisible) {
            fetchNextPageCities()
          } else if (isOriginModalVisible || isNationalitiesModalVisible) {
            fetchNextPageCountries()
          }
        }}
        hasNextPage={(isAddressModalVisible || isLivedPlacesModalVisible) ? hasNextPageCities : ((isOriginModalVisible || isNationalitiesModalVisible) ? hasNextPageCountries : false)}
        isFetchingNextPage={isFetchingNextPageCities || isFetchingNextPageCountries}
        onSearch={(text) => {
          if (isLivedPlacesModalVisible) setSearchText(text)
          else if (isAddressModalVisible) setResidenceModalSearch(text)
          else setSearchText(text)
        }}
      />

      <GenericSelectionModal
        isVisible={isResidenceContinentModalVisible}
        setIsVisible={setIsResidenceContinentModalVisible}
        title={t('filters.residenceContinent')}
        placeholder={t('filters.searchPlace')}
        availableItems={continentSelectionItems}
        selectedItems={filters.residenceContinents || []}
        onSelectionChange={(items) =>
          setFilters({
            ...filters,
            residenceContinents: items,
            residenceCountries: [],
            residenceRegions: [],
            residence: [],
          })
        }
        multiSelect
        fitParent={!isMobile}
        isFetching={isFetchingContinents}
        fetchNextPage={() => {}}
        hasNextPage={false}
        isFetchingNextPage={false}
        onSearch={(text) => setResidenceModalSearch(text)}
      />

      <GenericSelectionModal
        isVisible={isResidenceCountryModalVisible}
        setIsVisible={setIsResidenceCountryModalVisible}
        title={t('filters.residenceCountry')}
        placeholder={t('filters.searchPlace')}
        availableItems={countries.map((country) => ({
          id: country.code,
          name: country.name ?? country.nameFr,
        }))}
        selectedItems={filters.residenceCountries || []}
        onSelectionChange={(items) =>
          setFilters({
            ...filters,
            residenceCountries: items,
            residenceContinents: [],
            residenceRegions: [],
            residence: [],
          })
        }
        multiSelect
        fitParent={!isMobile}
        isFetching={isFetchingCountries}
        fetchNextPage={fetchNextPageCountries}
        hasNextPage={hasNextPageCountries}
        isFetchingNextPage={isFetchingNextPageCountries}
        onSearch={(text) => setResidenceModalSearch(text)}
      />

      <GenericSelectionModal
        isVisible={isResidenceRegionModalVisible}
        setIsVisible={setIsResidenceRegionModalVisible}
        title={t('filters.residenceRegion')}
        placeholder={t('filters.searchPlace')}
        availableItems={regions.map((r) => ({
          id: r.id,
          name: r.name ?? r.nameFr,
        }))}
        selectedItems={filters.residenceRegions || []}
        onSelectionChange={(items) =>
          setFilters({
            ...filters,
            residenceRegions: items,
            residenceContinents: [],
            residenceCountries: [],
            residence: [],
          })
        }
        multiSelect
        fitParent={!isMobile}
        isFetching={isFetchingRegions}
        fetchNextPage={fetchNextPageRegions}
        hasNextPage={hasNextPageRegions}
        isFetchingNextPage={isFetchingNextPageRegions}
        onSearch={(text) => setResidenceModalSearch(text)}
      />

      <GenericSelectionModal
        isVisible={isProfessionsModalVisible}
        setIsVisible={setIsProfessionsModalVisible}
        title={t('filters.activitySector')}
        placeholder={t('filters.searchSector')}
        availableItems={groupedProfessions}
        selectedItems={filters.professions}
        onSelectionChange={(professions) => {
          setFilters({ ...filters, professions })
        }}
        multiSelect
        fitParent={!isMobile}
        isFetching={isFetchingProfessions}
        isFetchingNextPage={isFetchingNextPageProfessions}
        hasNextPage={hasNextPageProfessions}
        fetchNextPage={fetchNextPageProfessions}
        onSearch={(search) => {
          setSearchText(search)
        }}
      />

      <GenericSelectionModal
        isVisible={isHashtagModalVisible}
        setIsVisible={setIsHashtagModalVisible}
        title={t('filters.searchHashtags')}
        placeholder={t('filters.searchHashtag')}
        availableItems={hashtags}
        selectedItems={filters.hashtags || []}
        onSelectionChange={(items) => {
          setFilters({ ...filters, hashtags: items })
        }}
        fitParent={!isMobile}
        isFetching={isFetchingHashtags}
        isFetchingNextPage={isFetchingNextPageHashtags}
        hasNextPage={hasNextPageHashtags}
        fetchNextPage={fetchNextPageHashtags}
        onSearch={(search) => {
          setSearchText(search)
        }}
      />

      <GenericSelectionModal
        isVisible={isCategoryModalVisible}
        setIsVisible={setIsCategoryModalVisible}
        title={t('filters.searchCategories')}
        placeholder={t('filters.searchCategory')}
        availableItems={categories.map(category => ({ id: category.id, name: category.name, photo: category.avatarUrl || '' }))}
        selectedItems={filters.categories || []}
        onSelectionChange={(items) => {
          setFilters({ ...filters, categories: items })
        }}
        fitParent={!isMobile}
        isFetching={isFetchingCategories}
        isFetchingNextPage={isFetchingNextPageCategories}
        hasNextPage={hasNextPageCategories}
        fetchNextPage={fetchNextPageCategories}
        onSearch={(search) => {
          setSearchText(search)
        }}
      />

      {!isSuggested && (
        <>
          <GenericSelectionModal
            isVisible={isContextModalVisible}
            setIsVisible={setIsContextModalVisible}
            title={t('filters.meetingContext')}
            placeholder={t('filters.searchMeetingContext')}
            availableItems={groupedContexts}
            selectedItems={filters.contexts || []}
            multiSelect
            onSelectionChange={(contexts) => {
              setFilters({ ...filters, contexts })
            }}
            fitParent={!isMobile}
          />

          <GenericSelectionModal
            isVisible={isEmotionalContextModalVisible}
            setIsVisible={setIsEmotionalContextModalVisible}
            title={t('filters.associatedEmotion')}
            placeholder={t('filters.searchEmotion')}
            availableItems={availableEmotionalContexts}
            selectedItems={filters.emotionalContexts || []}
            multiSelect
            onSelectionChange={(emotionalContexts) => {
              setFilters({ ...filters, emotionalContexts })
            }}
            fitParent={!isMobile}
          />

          <GenericSelectionModal
            isVisible={isRelationshipStatusModalVisible}
            setIsVisible={setIsRelationshipStatusModalVisible}
            title={t('filters.relationshipEvolution')}
            placeholder={t('filters.searchRelationship')}
            availableItems={availableRelationshipStatus}
            selectedItems={filters.relationshipStatus || []}
            multiSelect={true}
            onSelectionChange={(relationshipStatus) => {
              setFilters({ ...filters, relationshipStatus })
            }}
            fitParent={!isMobile}
          />

          <GenericSelectionModal
            isVisible={isRelationalColorModalVisible}
            setIsVisible={setIsRelationalColorModalVisible}
            title={t('filters.associatedColor')}
            placeholder={t('filters.searchColor')}
            availableItems={colorLabels}
            selectedItems={filters.relationalColor || []}
            multiSelect
            onSelectionChange={(relationalColor) => {
              setFilters({ ...filters, relationalColor })
            }}
            fitParent={!isMobile}
            isFetching={isFetchingColorLabels}
            isFetchingNextPage={isFetchingNextPageColorLabels}
            hasNextPage={hasNextPageColorLabels}
            fetchNextPage={fetchNextPageColorLabels}
            onSearch={(text) => setColorLabelSearch(text)}
          />
        </>
      )}

      <GenericSelectionModal
        isVisible={isLanguageModalVisible}
        setIsVisible={setIsLanguageModalVisible}
        title={t('filters.searchLanguage')}
        placeholder={t('filters.searchLanguage')}
        availableItems={allLanguages.map(language => ({ id: language.code, name: language.name ?? language.nameFr }))}
        selectedItems={filters.languages || []}
        onSelectionChange={(items) => {
          setFilters({ ...filters, languages: items })
        }}
        fitParent={!isMobile}
        isFetching={isFetchingLanguages}
        fetchNextPage={() => {
          fetchNextPageLanguages()
        }}
        hasNextPage={hasNextPageLanguages}
        isFetchingNextPage={isFetchingNextPageLanguages}
        onSearch={(text) => setSearchText(text)}
      />
    </Screen>
  )
}

// Styles identiques à AddContactsFilters
const getStyles = (theme: Theme, isNonMobile: boolean) =>
  StyleSheet.create({
    screen: {
      flex: 1,
      height: '100%',
      maxWidth: isNonMobile? 350 : undefined,
      justifyContent: 'center',
      borderLeftWidth: isNonMobile? 2 : 0,
      borderLeftColor: theme.colors.primary,
    },
    container: {
      flex: 1,
      backgroundColor: isNonMobile? theme.colors.sidebar : theme.colors.background,
      paddingHorizontal: isNonMobile? 0 : theme.spacing.lg,
      paddingTop: theme.spacing.md,
    },
    sectionContainer: {
      marginBottom: isNonMobile? 0 : theme.spacing.md,
      backgroundColor: isNonMobile? undefined : theme.colors.white,
      borderRadius: isNonMobile? 0 : theme.border.medium,
      shadowColor: theme.colors.black,
      shadowOffset: {
        width: 0,
        height: 1,
      },
      shadowOpacity: 0.05,
      shadowRadius: 2,
      elevation: 1,
    },
    sectionHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: theme.spacing.lg,
      paddingVertical: theme.spacing.md,
      minHeight: 56,
    },
    sectionHeaderLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
    },
    sectionTitle: {
      fontSize: isNonMobile? theme.fontSize.small : theme.fontSize.medium,
      fontWeight: '500',
      color: theme.colors.black,
      marginLeft: theme.spacing.md,
    },
    sectionValues: {
      fontSize: theme.fontSize.extraSmall,
      color: theme.colors.black,
      marginTop: theme.spacing.xs,
    },
    genderButton: {
      padding: 10,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.primary,
      marginHorizontal: 5,
      backgroundColor: theme.colors.white,
    },
    genderButtonActive: {
      backgroundColor: theme.colors.primary,
    },
    genderButtonText: {
      color: theme.colors.primary,
      fontWeight: '600',
    },
    ageInputContainer: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
    },
    ageInput: {
      fontSize: theme.fontSize.extraSmall,
      minWidth: 55,
      borderRadius: theme.fontSize.jumbo,
      paddingVertical: 6,
      textAlign: 'center',
      color: theme.colors.black,
      backgroundColor: '#F5F5F5',
    },
    degreeContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
      gap: theme.spacing.xs,
    },
    degreeChip: {
      paddingHorizontal: theme.spacing.sm,
      paddingVertical: theme.spacing.xs,
      borderRadius: theme.border.medium,
      borderWidth: 1,
      borderColor: theme.colors.lightGrey,
      backgroundColor: theme.colors.white,
    },
    degreeChipActive: {
      borderColor: theme.colors.primary,
      backgroundColor: theme.colors.primary + '20',
    },
    degreeChipText: {
      fontSize: theme.fontSize.extraSmall,
      color: theme.colors.greyBlue,
    },
    degreeChipTextActive: {
      color: theme.colors.primary,
      fontWeight: '600',
    },
    residenceAccordionBody: {
      paddingLeft: theme.spacing.lg,
      paddingRight: theme.spacing.lg,
      paddingBottom: theme.spacing.sm,
      borderTopWidth: isNonMobile ? 0 : 1,
      borderTopColor: theme.colors.lightGrey,
    },
    residenceSubRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: theme.spacing.sm,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
    },
    residenceSubRowText: {
      flex: 1,
      marginRight: theme.spacing.sm,
    },
    residenceSubRowLabel: {
      fontSize: theme.fontSize.small,
      fontWeight: '500',
      color: theme.colors.black,
    },
    residenceSubRowValues: {
      fontSize: theme.fontSize.extraSmall,
      color: theme.colors.greyBlue,
      marginTop: theme.spacing.xs,
    },
  })